import {useLayoutEffect, useRef, useState} from 'react'
import type React from 'react'

const MAX_VISIBLE_ITEMS = 8
const VIEWPORT_GAP = 4
const VIEWPORT_MARGIN = 8

type DropdownDirection = 'below' | 'above'

interface DropdownPosition {
  top: number
  left: number
  /** Set only when the popover matches its anchor's width; a content-sized menu sizes in CSS. */
  width?: number
  maxWidth: number
  maxHeight: number
}

export interface ViewportBounds {
  top: number
  right: number
  bottom: number
  left: number
}

interface AdaptiveDropdownPositionOptions {
  open: boolean
  anchorRef: React.RefObject<HTMLElement>
  popoverRef: React.RefObject<HTMLDivElement>
  itemSelector: string
  itemCount: number
  align?: 'start' | 'end'
  matchAnchorWidth?: boolean
  onAnchorHidden: () => void
}

/** The visible viewport (the visual viewport when the browser has one), in layout-viewport px. */
export function getViewportBounds(): ViewportBounds {
  const viewport = window.visualViewport
  const top = viewport?.offsetTop ?? 0
  const left = viewport?.offsetLeft ?? 0
  const width = viewport?.width ?? window.innerWidth
  const height = viewport?.height ?? window.innerHeight

  return {
    top,
    right: left + width,
    bottom: top + height,
    left,
  }
}

function measureVisibleItemHeights(popover: HTMLElement, itemSelector: string) {
  const items = Array.from(popover.querySelectorAll<HTMLElement>(itemSelector))
  const measuredItems = items.slice(0, MAX_VISIBLE_ITEMS)
  const computed = window.getComputedStyle(popover)
  const topBorder = Number.parseFloat(computed.borderTopWidth || '0')
  const bottomChrome =
    Number.parseFloat(computed.paddingBottom || '0') +
    Number.parseFloat(computed.borderBottomWidth || '0')

  const itemBottom = (item: HTMLElement) => {
    if (item.offsetHeight) {
      return topBorder + item.offsetTop + item.offsetHeight + bottomChrome
    }

    const popoverRect = popover.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    return itemRect.bottom - popoverRect.top + bottomChrome
  }

  const naturalHeight = popover.scrollHeight || popover.getBoundingClientRect().height
  const oneItemHeight = measuredItems[0] ? itemBottom(measuredItems[0]) : naturalHeight
  const lastMeasuredItem = measuredItems.at(-1)
  const cappedHeight = lastMeasuredItem ? itemBottom(lastMeasuredItem) : naturalHeight

  return {oneItemHeight, cappedHeight}
}

/**
 * Positions a portaled dropdown below its trigger when one row fits, otherwise
 * above it. The selected direction remains stable until the dropdown closes.
 */
export function useAdaptiveDropdownPosition({
  open,
  anchorRef,
  popoverRef,
  itemSelector,
  itemCount,
  align = 'start',
  matchAnchorWidth = false,
  onAnchorHidden,
}: AdaptiveDropdownPositionOptions) {
  const [position, setPosition] = useState<DropdownPosition | null>(null)
  const directionRef = useRef<DropdownDirection | null>(null)
  const onAnchorHiddenRef = useRef(onAnchorHidden)
  onAnchorHiddenRef.current = onAnchorHidden

  useLayoutEffect(() => {
    if (!open) {
      directionRef.current = null
      setPosition(null)
      return
    }

    const update = () => {
      const anchor = anchorRef.current
      const popover = popoverRef.current
      if (!anchor || !popover) return

      const viewport = getViewportBounds()
      const anchorRect = anchor.getBoundingClientRect()
      // An all-zero rect means the anchor has no layout information (e.g. jsdom, or an
      // element that has not been laid out yet); it is not evidence the trigger scrolled
      // out of view, so it must not close the popover.
      const anchorHasLayout = anchorRect.width > 0 || anchorRect.height > 0
      const anchorVisible =
        anchorRect.bottom > viewport.top &&
        anchorRect.top < viewport.bottom &&
        anchorRect.right > viewport.left &&
        anchorRect.left < viewport.right

      if (anchorHasLayout && !anchorVisible) {
        onAnchorHiddenRef.current()
        return
      }

      const availableWidth = Math.max(0, viewport.right - viewport.left - VIEWPORT_MARGIN * 2)
      // A content-sized menu keeps its width in CSS. Its rendered width is read here only to
      // place `left` and is never written back: a self-measured width (scrollWidth leaves out
      // the borders and scrollbar) made the menu narrower on every scroll and resize.
      const measuredWidth = matchAnchorWidth
        ? anchorRect.width
        : popover.getBoundingClientRect().width
      const width = Math.min(measuredWidth, availableWidth)
      const desiredLeft = align === 'end' ? anchorRect.right - width : anchorRect.left
      const left = Math.min(
        Math.max(desiredLeft, viewport.left + VIEWPORT_MARGIN),
        viewport.right - VIEWPORT_MARGIN - width,
      )

      const spaceBelow = Math.max(
        0,
        viewport.bottom - VIEWPORT_MARGIN - anchorRect.bottom - VIEWPORT_GAP,
      )
      const spaceAbove = Math.max(
        0,
        anchorRect.top - VIEWPORT_GAP - (viewport.top + VIEWPORT_MARGIN),
      )
      const {oneItemHeight, cappedHeight} = measureVisibleItemHeights(popover, itemSelector)

      if (!directionRef.current) {
        directionRef.current =
          spaceBelow >= oneItemHeight || spaceBelow >= spaceAbove ? 'below' : 'above'
      }

      const direction = directionRef.current
      const availableHeight = direction === 'below' ? spaceBelow : spaceAbove
      const maxHeight = Math.min(cappedHeight, availableHeight)
      const desiredTop =
        direction === 'below'
          ? anchorRect.bottom + VIEWPORT_GAP
          : anchorRect.top - VIEWPORT_GAP - maxHeight
      const top = Math.min(
        Math.max(desiredTop, viewport.top + VIEWPORT_MARGIN),
        viewport.bottom - VIEWPORT_MARGIN - maxHeight,
      )

      setPosition({
        top,
        left,
        width: matchAnchorWidth ? width : undefined,
        maxWidth: availableWidth,
        maxHeight,
      })
    }

    update()
    const animationFrame = window.requestAnimationFrame?.(update)
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)
    window.visualViewport?.addEventListener('resize', update)

    return () => {
      if (animationFrame !== undefined) window.cancelAnimationFrame?.(animationFrame)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('scroll', update)
      window.visualViewport?.removeEventListener('resize', update)
    }
  }, [open, anchorRef, popoverRef, itemSelector, itemCount, align, matchAnchorWidth])

  return position
}
